/** Client-side email domain checks via public DNS-over-HTTPS (runs in the browser). */

export type DomainCheckStatus = 'pending' | 'ok' | 'warn' | 'fail';

export type EmailDomainCheckItem = {
  id: string;
  label: string;
  status: DomainCheckStatus;
  detail: string;
};

export type EmailDomainHealthResult = {
  domain: string | null;
  checks: EmailDomainCheckItem[];
  hasFailure: boolean;
  hasWarning: boolean;
};

const GOOGLE_DNS = 'https://dns.google/resolve';
const HTTPS_TIMEOUT_MS = 3500;
const RDAP_TIMEOUT_MS = 5000;

const DNS_TYPE = { MX: 15, A: 1, AAAA: 28 } as const;

type DnsResponse = {
  Status: number;
  Answer?: { data?: string }[];
};

export function extractEmailDomain(
  email: string | null | undefined
): string | null {
  if (!email) return null;
  const match = email.trim().match(/@([^@\s]+)$/i);
  return match ? match[1].toLowerCase() : null;
}

function summarizeResult(
  domain: string | null,
  checks: EmailDomainCheckItem[]
): EmailDomainHealthResult {
  return {
    domain,
    checks,
    hasFailure: checks.some((c) => c.status === 'fail'),
    hasWarning: checks.some((c) => c.status === 'warn'),
  };
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
}

async function dnsLookup(
  name: string,
  type: keyof typeof DNS_TYPE
): Promise<DnsResponse> {
  const url = `${GOOGLE_DNS}?name=${encodeURIComponent(name)}&type=${DNS_TYPE[type]}`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`DNS lookup failed (${res.status})`);
  }
  return res.json() as Promise<DnsResponse>;
}

function dnsHasAnswers(response: DnsResponse): boolean {
  return (response.Answer?.length ?? 0) > 0;
}

function isNxDomain(response: DnsResponse): boolean {
  return response.Status === 3;
}

async function checkMx(domain: string): Promise<EmailDomainCheckItem> {
  try {
    const response = await dnsLookup(domain, 'MX');
    if (isNxDomain(response)) {
      return {
        id: 'mx',
        label: 'MX records',
        status: 'fail',
        detail: 'Domain does not exist (NXDOMAIN)',
      };
    }
    if (!dnsHasAnswers(response)) {
      return {
        id: 'mx',
        label: 'MX records',
        status: 'fail',
        detail: 'No mail exchanger records',
      };
    }
    const hosts = (response.Answer ?? [])
      .map((a) => a.data?.replace(/\.$/, '') ?? '')
      .filter(Boolean)
      .slice(0, 3);
    return {
      id: 'mx',
      label: 'MX records',
      status: 'ok',
      detail: hosts.join(', ') || 'Present',
    };
  } catch (e) {
    return {
      id: 'mx',
      label: 'MX records',
      status: 'warn',
      detail: e instanceof Error ? e.message : 'Lookup failed',
    };
  }
}

async function checkResolves(domain: string): Promise<EmailDomainCheckItem> {
  try {
    const [a, aaaa] = await Promise.all([
      dnsLookup(domain, 'A'),
      dnsLookup(domain, 'AAAA'),
    ]);
    const nx = isNxDomain(a) && isNxDomain(aaaa);
    if (nx) {
      return {
        id: 'resolve',
        label: 'Domain resolves',
        status: 'fail',
        detail: 'No A or AAAA records (NXDOMAIN)',
      };
    }
    const hasA = dnsHasAnswers(a);
    const hasAaaa = dnsHasAnswers(aaaa);
    if (!hasA && !hasAaaa) {
      return {
        id: 'resolve',
        label: 'Domain resolves',
        status: 'fail',
        detail: 'No A or AAAA records',
      };
    }
    const parts: string[] = [];
    if (hasA) parts.push('A');
    if (hasAaaa) parts.push('AAAA');
    return {
      id: 'resolve',
      label: 'Domain resolves',
      status: 'ok',
      detail: parts.join(' + ') || 'Present',
    };
  } catch (e) {
    return {
      id: 'resolve',
      label: 'Domain resolves',
      status: 'warn',
      detail: e instanceof Error ? e.message : 'Lookup failed',
    };
  }
}

function domainHasNoDns(mx: EmailDomainCheckItem, resolve: EmailDomainCheckItem): boolean {
  const mxGone =
    mx.status === 'fail' &&
    (mx.detail.includes('NXDOMAIN') || mx.detail.includes('No mail'));
  const resolveGone =
    resolve.status === 'fail' &&
    (resolve.detail.includes('NXDOMAIN') || resolve.detail.includes('No A or AAAA'));
  return mxGone && resolveGone;
}

async function tryHttpsUrl(url: string): Promise<string | null> {
  try {
    await fetchWithTimeout(
      url,
      { method: 'GET', mode: 'no-cors' },
      HTTPS_TIMEOUT_MS
    );
    return url.replace('https://', '');
  } catch {
    return null;
  }
}

async function checkHttps(
  domain: string,
  skipDetail?: string
): Promise<EmailDomainCheckItem> {
  if (skipDetail) {
    return {
      id: 'https',
      label: 'HTTPS reachable',
      status: 'fail',
      detail: skipDetail,
    };
  }

  const urls = [`https://${domain}`, `https://www.${domain}`];
  const hits = await Promise.all(urls.map((url) => tryHttpsUrl(url)));
  const host = hits.find(Boolean);
  if (host) {
    return {
      id: 'https',
      label: 'HTTPS reachable',
      status: 'ok',
      detail: host,
    };
  }
  return {
    id: 'https',
    label: 'HTTPS reachable',
    status: 'fail',
    detail: 'No response at apex or www',
  };
}

type RdapEvent = { eventAction?: string; eventDate?: string };

async function checkRegistration(
  domain: string,
  skipDetail?: string
): Promise<EmailDomainCheckItem> {
  if (skipDetail) {
    return {
      id: 'registration',
      label: 'Registration',
      status: 'fail',
      detail: skipDetail,
    };
  }

  try {
    const res = await fetchWithTimeout(
      `https://rdap.org/domain/${encodeURIComponent(domain.toUpperCase())}`,
      { headers: { Accept: 'application/rdap+json' } },
      RDAP_TIMEOUT_MS
    );
    if (res.status === 404) {
      return {
        id: 'registration',
        label: 'Registration',
        status: 'fail',
        detail: 'Not found in RDAP',
      };
    }
    if (!res.ok) {
      return {
        id: 'registration',
        label: 'Registration',
        status: 'warn',
        detail: `RDAP returned ${res.status}`,
      };
    }
    const data = (await res.json()) as { events?: RdapEvent[] };
    const events = data.events ?? [];
    const expiration = events.find((event) => {
      const action = (event.eventAction ?? '').toLowerCase();
      return action.includes('expiration');
    });
    if (!expiration?.eventDate) {
      return {
        id: 'registration',
        label: 'Registration',
        status: 'ok',
        detail: 'Listed in RDAP',
      };
    }
    const expDate = new Date(expiration.eventDate);
    if (Number.isNaN(expDate.getTime())) {
      return {
        id: 'registration',
        label: 'Registration',
        status: 'warn',
        detail: 'Expiration date unreadable',
      };
    }
    if (expDate.getTime() < Date.now()) {
      return {
        id: 'registration',
        label: 'Registration',
        status: 'fail',
        detail: `Expired ${expDate.toLocaleDateString()}`,
      };
    }
    return {
      id: 'registration',
      label: 'Registration',
      status: 'ok',
      detail: `Expires ${expDate.toLocaleDateString()}`,
    };
  } catch (e) {
    const timedOut = e instanceof DOMException && e.name === 'AbortError';
    return {
      id: 'registration',
      label: 'Registration',
      status: 'warn',
      detail: timedOut ? 'RDAP timed out' : 'RDAP unavailable from browser',
    };
  }
}

const PENDING_HTTPS: EmailDomainCheckItem = {
  id: 'https',
  label: 'HTTPS reachable',
  status: 'pending',
  detail: 'Checking…',
};

const PENDING_REGISTRATION: EmailDomainCheckItem = {
  id: 'registration',
  label: 'Registration',
  status: 'pending',
  detail: 'Checking…',
};

const PENDING_CHECKS: EmailDomainCheckItem[] = [
  { id: 'mx', label: 'MX records', status: 'pending', detail: 'Checking…' },
  {
    id: 'resolve',
    label: 'Domain resolves',
    status: 'pending',
    detail: 'Checking…',
  },
  PENDING_HTTPS,
  PENDING_REGISTRATION,
];

export type EmailDomainHealthProgress = EmailDomainHealthResult;

export async function runEmailDomainHealthChecks(
  email: string | null | undefined,
  onProgress?: (snapshot: EmailDomainHealthProgress) => void
): Promise<EmailDomainHealthResult> {
  const domain = extractEmailDomain(email);
  const report = (checks: EmailDomainCheckItem[]) => {
    const snapshot = summarizeResult(domain, checks);
    onProgress?.(snapshot);
    return snapshot;
  };

  if (!domain) {
    return report([
      {
        id: 'email',
        label: 'Email',
        status: 'fail',
        detail: 'No valid email on contact',
      },
    ]);
  }

  report(PENDING_CHECKS);

  const [mx, resolve] = await Promise.all([
    checkMx(domain),
    checkResolves(domain),
  ]);

  const noDns = domainHasNoDns(mx, resolve);
  const httpsSkip = noDns ? 'Skipped — domain has no DNS' : undefined;
  const rdapSkip = noDns ? 'Not in RDAP (no DNS)' : undefined;

  report([mx, resolve, PENDING_HTTPS, PENDING_REGISTRATION]);

  const [https, registration] = await Promise.all([
    checkHttps(domain, httpsSkip),
    checkRegistration(domain, rdapSkip),
  ]);

  return report([mx, resolve, https, registration]);
}

export { PENDING_CHECKS };
